/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import remove from 'lodash.remove';
import { action, extendObservable, observable } from 'mobx';
import { createEntityStore } from 'src/luis/core/stores/entityStore';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { HierarchicalEntityStore } from 'src/luis/types/stores/entities/HierarchicalEntityStore';
import { SubtractStore } from 'src/luis/types/SubtractStore';

/**
 * A hierarchical entity store factory function.
 *
 * @param id The id of the entity to create.
 * @param entity The hierarchical entity data.
 * @returns A new instance of a hierarchical entity store.
 */
export const createHierarchicalEntityStore = (id: LuisId, entity: Partial<HierarchicalEntityStore>): HierarchicalEntityStore => {
    const entityStore = createEntityStore(id, { ...entity, typeId: ModelType.Hierarchical });

    const hierarchicalEntityStore: SubtractStore<HierarchicalEntityStore, EntityStore> = {
        children: entity.children !== undefined ? entity.children : [],

        /**
         * Removes the child that has the given id from the
         * array of children of the entity.
         *
         * @param childId The id of the child to delete.
         * @returns True if deletion was successful and
         * false otherwise.
         */
        removeChildById(childIdToRemove) {
            return remove(this.children, child => child.id === childIdToRemove).length > 0;
        },

        /**
         * Renames the child that has the given id from the
         * array of children of the entity.
         *
         * @param childId The id of the child to rename.
         * @param newName The name of the child to rename.
         */
        renameChild(childId, newName) {
            const childIndex = this.children.findIndex(c => c.id === childId);
            this.children[childIndex].name = newName;
        }
    };

    return extendObservable(entityStore, hierarchicalEntityStore, { children: observable, removeChildById: action }, { deep: false });
};
